import { create } from 'zustand';
import { getOrganizations as getOrganizationsRequest, getOrganization, createOrganization, updateOrganization, updateOrganizationPilot as updateOrganizationPilotRequest, deleteOrganization } from '../services/organization.service';
import { OrganizationSchema, OrganizationRequest, UpdateOrganizationRequest, OrganizationPagination, OrganizationQueryParams } from '../types/organization.type';
import { parseApiError } from '../helpers/api-error.helper';

interface OrganizationState {
  organizations: OrganizationSchema[];
  total: number;
  page: number;
  limit: number;
  selectedOrganization: OrganizationSchema | null;
  isLoading: boolean;
  pilotUpdatingOrganizationIds: string[];
  error: string | null;
  showOrganizationModal: boolean;
  setSelectedOrganization: (organization: OrganizationSchema | null) => void;
  setShowOrganizationModal: (show: boolean) => void;
  getOrganizations: (params?: OrganizationQueryParams) => Promise<OrganizationPagination | null>;
  getAllOrganizations: (params?: Omit<OrganizationQueryParams, 'page' | 'limit'>) => Promise<OrganizationSchema[]>;
  fetchOrganizationById: (organizationId: string) => Promise<OrganizationSchema | null>;
  createOrganization: (organization: OrganizationRequest) => Promise<OrganizationSchema | null>;
  updateOrganization: (organizationId: string, organization: UpdateOrganizationRequest) => Promise<OrganizationSchema | null>;
  updateOrganizationPilot: (organizationId: string, isPilot: boolean) => Promise<OrganizationSchema | null>;
  deleteOrganization: (organizationId: string) => Promise<boolean>;
}

interface GenericResponse<T> {
  error?: {
    message?: string;
    code?: string;
  } | null;
  response?: {
    message?: string;
    code?: string;
  } | null;
  result: T;
}

/** The organization picked in the sidebar, restored before the first render so permission gates see it. */
function readStoredOrganization(): OrganizationSchema | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const saved = sessionStorage.getItem('selectedOrganization');
    return saved ? (JSON.parse(saved) as OrganizationSchema) : null;
  } catch {
    sessionStorage.removeItem('selectedOrganization');
    return null;
  }
}

export const useOrganizationStore = create<OrganizationState>((set) => ({
  organizations: [],
  total: 0,
  page: 1,
  limit: 10,
  selectedOrganization: readStoredOrganization(),
  isLoading: false,
  pilotUpdatingOrganizationIds: [],
  error: null,
  showOrganizationModal: false,
  setShowOrganizationModal: (show: boolean) => set({ showOrganizationModal: show }),
  setSelectedOrganization: (organization) => set({ selectedOrganization: organization }),
  
  getOrganizations: async (params?: OrganizationQueryParams) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getOrganizationsRequest(params);
      const data = response as GenericResponse<OrganizationPagination>;
      set({
        organizations: data.result.organizations,
        total: data.result.total,
        page: data.result.page,
        limit: data.result.limit,
        isLoading: false
      });
      return data.result;
    } catch (error: any) {
      set({ error: error.response?.data?.["detail"] || 'Failed to get organizations', isLoading: false });
      return null;
    }
  },

  getAllOrganizations: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const all = new Map<string, OrganizationSchema>();
      const limit = 100;
      let page = 1;
      let total = Number.POSITIVE_INFINITY;

      while (page <= 100 && all.size < total) {
        const response = await getOrganizationsRequest({ ...params, page, limit });
        const data = response as GenericResponse<OrganizationPagination>;
        const batch = data.result?.organizations ?? [];
        total = data.result?.total ?? batch.length;
        batch.forEach((organization) => all.set(organization.id, organization));

        if (batch.length === 0 || batch.length < limit) break;
        page += 1;
      }

      const organizations = Array.from(all.values());
      set({
        organizations,
        total: Number.isFinite(total) ? total : organizations.length,
        page: 1,
        limit: organizations.length,
        isLoading: false,
      });
      return organizations;
    } catch (error: any) {
      set({
        error: error.response?.data?.['detail'] || 'Failed to get organizations',
        isLoading: false,
      });
      return [];
    }
  },

  fetchOrganizationById: async (organizationId) => {
    set({ error: null });
    try {
      const response = await getOrganization(organizationId);
      const data = response as GenericResponse<OrganizationSchema>;
      return data.result;
    } catch (error: any) {
      set({
        error: error.response?.data?.["detail"] || "Failed to load organization",
      });
      return null;
    }
  },

  createOrganization: async (organization) => {
    set({ isLoading: true, error: null });
    try {
      const response = await createOrganization(organization);
      const data = response as GenericResponse<OrganizationSchema>;
      
      set((state) => ({ 
        organizations: [...state.organizations, data.result],
        isLoading: false 
      }));

      return data.result;
    } catch (error: any) {
      set({ error: error.response?.data?.["detail"] || 'Failed to create organization', isLoading: false });
      return null;
    }
  },

  updateOrganization: async (organizationId, organization) => {
    set({ isLoading: true, error: null });
    try {
      const response = await updateOrganization(organizationId, organization);
      const data = response as GenericResponse<OrganizationSchema>;
      
      set((state) => ({ 
        organizations: state.organizations.map(org => 
          org.id === organizationId ? data.result : org
        ),
        isLoading: false 
      }));

      return data.result;
    } catch (error: any) {
      set({ error: error.response?.data?.["detail"] || 'Failed to update organization', isLoading: false });
      return null;
    }
  },

  updateOrganizationPilot: async (organizationId, isPilot) => {
    set((state) => ({
      error: null,
      pilotUpdatingOrganizationIds: state.pilotUpdatingOrganizationIds.includes(organizationId)
        ? state.pilotUpdatingOrganizationIds
        : [...state.pilotUpdatingOrganizationIds, organizationId],
    }));
    try {
      const response = await updateOrganizationPilotRequest(organizationId, {
        is_pilot: isPilot,
      });
      const data = response as GenericResponse<OrganizationSchema>;
      set((state) => ({
        organizations: state.organizations.map((org) =>
          org.id === organizationId ? data.result : org
        ),
        selectedOrganization:
          state.selectedOrganization?.id === organizationId
            ? data.result
            : state.selectedOrganization,
        pilotUpdatingOrganizationIds: state.pilotUpdatingOrganizationIds.filter(
          (id) => id !== organizationId
        ),
      }));
      return data.result;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: unknown } } };
      const parsed = parseApiError(
        err.response?.data?.detail,
        'Failed to update pilot status',
      );
      set((state) => ({
        error: parsed.message,
        pilotUpdatingOrganizationIds: state.pilotUpdatingOrganizationIds.filter(
          (id) => id !== organizationId
        ),
      }));
      return null;
    }
  },

  deleteOrganization: async (organizationId) => {
    set({ isLoading: true, error: null });
    try {
      await deleteOrganization(organizationId);
      set((state) => ({ 
        organizations: state.organizations.filter(org => org.id !== organizationId),
        isLoading: false 
      }));
      return true;
    } catch (error: any) {
      set({ error: error.response?.data?.["detail"] || 'Failed to delete organization', isLoading: false });
      return false;
    }
  },
}));
